import { DocDeltaFormat, DocBlockContent, DocBlock, Doc } from "interfaces/feature-request";
import React from "react";
import styled from "styled-components";
import styles from "styles/styles";

const DocContainer = styled.div`
    color: ${styles.colors.black};
`;

const LargeTitle = styled.h3<{ textColor: string }>`
    font-size: 18px;
    margin: 0.5rem 0 0.5rem 0;
    color: ${(props) => props.textColor};
`;

const NormalText = styled.p<{ textColor: string }>`
    font-size: 16px;
    margin: 0.5rem 0;
    color: ${(props) => props.textColor};
`;

const BulletedList = styled.ul`
    margin: 0.5rem 0;
    padding-left: 1.5rem;
`;

const ListItem = styled.li<{ textColor: string }>`
    font-size: 16px;
    margin: 0.25rem 0;
    color: ${(props) => props.textColor};
`;

const Quote = styled.blockquote<{ textColor: string }>`
    background-color: ${styles.colors.lightGrey};
    border-left: 4px solid ${styles.colors.grey2};
    margin: 1rem 0;
    padding: 1rem;
    font-style: italic;
    color: ${(props) => props.textColor};
`;

const CodeBlock = styled.pre`
    background-color: ${styles.colors.lightGrey};
    border: 1px solid ${styles.colors.grey2};
    padding: 1rem;
    margin: 1rem 0;
    overflow-x: auto;
    font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
    font-size: 14px;
`;

const mapMondayColor = (deltaFormat: DocDeltaFormat[]): string => {
    const mondayColor = deltaFormat.find((delta) => delta.attributes?.color)?.attributes?.color;

    return !mondayColor ? styles.colors.grey7 : styles.colors.red;
};

interface FeatureRequestSolutionDescriptionProps {
    solutionDescription: Record<string, unknown>;
}

const parseContent = (blockContentString: string): DocBlockContent =>
    JSON.parse(blockContentString);

const extractTextFromDelta = (deltaFormat: DocDeltaFormat[]): { text: string; color: string } =>
    !deltaFormat || deltaFormat.length === 0
        ? { text: "", color: styles.colors.grey7 }
        : {
              text: deltaFormat.map((delta) => delta.insert).join(""),
              color: mapMondayColor(deltaFormat),
          };

const renderBlock = (block: DocBlock, index: number): React.ReactNode => {
    const content = parseContent(block.content);
    const { text, color } = extractTextFromDelta(content.deltaFormat || []);

    switch (block.type) {
        case "large title":
            return (
                <LargeTitle key={block.id} textColor={color}>
                    {text}
                </LargeTitle>
            );

        case "normal text":
            return (
                <NormalText key={block.id} textColor={color}>
                    {text}
                </NormalText>
            );

        case "bulleted list":
            return (
                <BulletedList key={`list-${index}`}>
                    <ListItem textColor={color}>{text}</ListItem>
                </BulletedList>
            );

        case "quote":
            return (
                <Quote key={block.id} textColor={color}>
                    {text}
                </Quote>
            );

        case "code":
            return <CodeBlock key={block.id}>{text}</CodeBlock>;

        case "widget":
            return null;

        default:
            return (
                <NormalText key={block.id} textColor={color}>
                    {text}
                </NormalText>
            );
    }
};

const renderBulletListGroup = (bulletBlocks: DocBlock[], key: string): React.ReactNode => (
    <BulletedList key={key}>
        {bulletBlocks.map((bulletBlock) => {
            const { text, color } = extractTextFromDelta(
                parseContent(bulletBlock.content).deltaFormat || []
            );

            return (
                <ListItem key={bulletBlock.id} textColor={color}>
                    {text}
                </ListItem>
            );
        })}
    </BulletedList>
);

const groupBlocksByType = (blocks: DocBlock[]): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    let currentBulletGroup: DocBlock[] = [];
    let bulletGroupIndex = 0;

    const flushBulletGroup = () => {
        if (currentBulletGroup.length > 0) {
            result.push(
                renderBulletListGroup(currentBulletGroup, `bullet-group-${bulletGroupIndex++}`)
            );
            currentBulletGroup = [];
        }
    };

    blocks.forEach((block, index) => {
        if (block.type === "bulleted list") {
            currentBulletGroup.push(block);
        } else {
            flushBulletGroup();

            const renderedBlock = renderBlock(block, index);

            if (renderedBlock) {
                result.push(renderedBlock);
            }
        }
    });

    flushBulletGroup();

    return result;
};

const FeatureRequestSolutionDescription: React.FC<FeatureRequestSolutionDescriptionProps> = ({
    solutionDescription,
}) => (
    <DocContainer>{groupBlocksByType((solutionDescription as unknown as Doc).blocks)}</DocContainer>
);

export default FeatureRequestSolutionDescription;
